import React, { useCallback, useMemo } from "react";

import { usePredictionsForFile } from "@/store";
import { MediaItem } from "@/types/media";

interface TimelinePredictionMarkersProps {
    mediaItem: MediaItem;
    onMarkerClick?: (targetTime: number, segment: number) => void;
    onMarkerHoverChange?: (hovering: boolean) => void;
}

export const TimelinePredictionMarkers: React.FC<TimelinePredictionMarkersProps> = ({
    mediaItem,
    onMarkerClick,
    onMarkerHoverChange
}) => {
    const filePredictions = usePredictionsForFile(mediaItem.id);

    const videoFps = mediaItem.metadata?.videoMetadata?.fps || 30;
    const fileDuration = mediaItem.metadata?.videoMetadata?.duration || 0;
    const keyframeOffset = mediaItem.metadata?.keyframe_offset || 0;

    const markerPositions = useMemo(() => {
        if (fileDuration === 0) return [];

        return Object.entries(filePredictions)
            .filter(([, predictions]) => predictions !== undefined)
            .map(([frameSegment]) => {
                const segment = parseInt(frameSegment, 10);
                const timeInVideo = segment / videoFps;
                const percent = (timeInVideo / fileDuration) * 100;
                return {
                    segment,
                    percent: Math.max(0, Math.min(percent, 100)),
                    absoluteTime: timeInVideo + keyframeOffset
                };
            });
    }, [filePredictions, videoFps, fileDuration, keyframeOffset]);

    const handleClick = useCallback(
        (e: React.MouseEvent, absoluteTime: number, segment: number) => {
            e.stopPropagation();
            onMarkerClick?.(absoluteTime, segment);
        },
        [onMarkerClick]
    );

    if (markerPositions.length === 0) return null;

    return (
        <>
            {markerPositions.map(({ segment, percent, absoluteTime }) => (
                <div
                    key={segment}
                    className="group absolute z-[30] -translate-x-1/2 cursor-pointer"
                    style={{ left: `${percent}%`, top: 0, height: "100%" }}
                    onClick={(e) => handleClick(e, absoluteTime, segment)}
                    onMouseEnter={() => onMarkerHoverChange?.(true)}
                    onMouseLeave={() => onMarkerHoverChange?.(false)}
                >
                    {/* Circle — floats above the timeline */}
                    <div className="absolute -top-2.5 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-brand bg-purboflow-100 group-hover:bg-brand-hover" />
                    {/* Line — spans full timeline height */}
                    <div className="absolute bottom-0 left-1/2 top-0 w-1 -translate-x-1/2 border border-brand bg-purboflow-100 group-hover:bg-brand-hover" />
                </div>
            ))}
        </>
    );
};
